/**
 * Credentials and evidence (wave CRED rulings 15 to 21): the credential register, its
 * status acts and an instrument's impact list; the measured value on a conduct line; the
 * EN 10204 classification of a file; a contract's staffing rules and shortfalls.
 *
 * Author: Mahmoud Ragab Ahmed.
 *
 * The register is read by the operator (OWNER READ) and by a counterparty for its own
 * party's people on its own contracts; recording and the status acts are CREDENTIAL
 * MANAGE (the QC lead and the head at tenant in the shipped policy), 404 by absence to
 * anyone else. EXPIRED is never stored: the server reads it off the validity date, and
 * the client derives it the same way for a row it already holds.
 */

import { apiJson, bool, isRecord, num, optStr, query, str, strArray } from "./client";

export type HolderKind = "PERSON" | "SUPPLIER" | "INSTRUMENT";
export type CredentialStatus = "VALID" | "EXPIRED" | "SUSPENDED" | "REVOKED";

export interface CredentialType {
  readonly id: string;
  readonly code: string;
  readonly title: string;
  readonly holderKind: HolderKind;
  readonly requiresEvidence: boolean;
  readonly validityRequired: boolean;
}

export interface Credential {
  readonly id: string;
  readonly typeCode: string;
  readonly typeTitle: string;
  readonly holderKind: HolderKind;
  readonly holderPersonId: string | null;
  readonly holderPersonName: string | null;
  readonly holderPartyId: string | null;
  readonly holderPartyName: string | null;
  readonly instrumentTag: string | null;
  readonly reference: string;
  readonly issuedOn: string;
  readonly validUntil: string | null;
  readonly issuer: string | null;
  readonly status: string;
  readonly statusReason: string | null;
  readonly evidenceAttachmentIds: readonly string[];
}

function holderKind(raw: unknown): HolderKind {
  return raw === "SUPPLIER" || raw === "INSTRUMENT" ? raw : "PERSON";
}

function credentialType(one: Record<string, unknown>): CredentialType {
  return {
    id: str(one.id),
    code: str(one.code),
    title: str(one.title),
    holderKind: holderKind(one.holder_kind),
    requiresEvidence: bool(one.requires_evidence),
    validityRequired: bool(one.validity_required),
  };
}

function credential(raw: unknown): Credential {
  const one = isRecord(raw) ? raw : {};
  return {
    id: str(one.id),
    typeCode: str(one.type_code),
    typeTitle: str(one.type_title),
    holderKind: holderKind(one.holder_kind),
    holderPersonId: optStr(one.holder_person_id),
    holderPersonName: optStr(one.holder_person_name),
    holderPartyId: optStr(one.holder_party_id),
    holderPartyName: optStr(one.holder_party_name),
    instrumentTag: optStr(one.instrument_tag),
    reference: str(one.reference),
    issuedOn: str(one.issued_on),
    validUntil: optStr(one.valid_until),
    issuer: optStr(one.issuer),
    status: str(one.status),
    statusReason: optStr(one.status_reason),
    evidenceAttachmentIds: strArray(one.evidence_attachment_ids ?? []),
  };
}

/** Today in the browser's calendar, as the date the validity is compared with. */
export function todayIso(now: Date = new Date()): string {
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const date = String(now.getDate()).padStart(2, "0");
  return `${String(now.getFullYear())}-${month}-${date}`;
}

/**
 * The status a chip shows: the server's, except that a VALID row past its validity date
 * reads EXPIRED (never stored; the server reads it the same way at the moment of use).
 */
export function displayStatus(row: Credential, today: string = todayIso()): CredentialStatus {
  if (row.status === "VALID" && row.validUntil !== null && row.validUntil < today) {
    return "EXPIRED";
  }
  return row.status === "SUSPENDED" || row.status === "REVOKED" || row.status === "EXPIRED"
    ? row.status
    : "VALID";
}

/** The holder in words: the person, the supplier party or the instrument's tag. */
export function holderName(row: Credential): string {
  if (row.holderKind === "INSTRUMENT") {
    return row.instrumentTag ?? "";
  }
  if (row.holderKind === "SUPPLIER") {
    return row.holderPartyName ?? "";
  }
  return row.holderPersonName ?? "";
}

export async function listCredentialTypes(
  token: string,
  signal?: AbortSignal,
): Promise<CredentialType[]> {
  return apiJson(
    token,
    "/credential-types",
    (raw) => (Array.isArray(raw) ? raw : []).filter(isRecord).map(credentialType),
    { signal },
  );
}

export interface CredentialFilter {
  readonly holderKind?: HolderKind | null | undefined;
  readonly holderPersonId?: string | null | undefined;
  readonly holderPartyId?: string | null | undefined;
  readonly signal?: AbortSignal | undefined;
}

export async function listCredentials(
  token: string,
  filter: CredentialFilter = {},
): Promise<Credential[]> {
  return apiJson(
    token,
    `/credentials${query({
      holder_kind: filter.holderKind,
      holder_person_id: filter.holderPersonId,
      holder_party_id: filter.holderPartyId,
    })}`,
    (raw) => {
      const body = isRecord(raw) ? raw : {};
      return (Array.isArray(body.items) ? body.items : []).map(credential);
    },
    { signal: filter.signal },
  );
}

/** The record body on the wire (CredentialCreate). */
export interface CredentialPayload {
  readonly type_code: string;
  readonly holder_person_id: string | null;
  readonly holder_party_id: string | null;
  readonly instrument_tag: string | null;
  readonly reference: string;
  readonly issued_on: string;
  readonly valid_until: string | null;
  readonly issuer: string | null;
  readonly evidence_attachment_ids: readonly string[];
}

export async function recordCredential(
  token: string,
  payload: CredentialPayload,
): Promise<Credential> {
  return apiJson(token, "/credentials", credential, { method: "POST", body: payload });
}

/** Suspend (SUSPENDED), revoke (REVOKED) or restore (VALID); a reason is required to stop. */
export async function setCredentialStatus(
  token: string,
  credentialId: string,
  payload: { readonly status: "VALID" | "SUSPENDED" | "REVOKED"; readonly reason: string | null },
): Promise<Credential> {
  return apiJson(token, `/credentials/${credentialId}/status`, credential, {
    method: "POST",
    body: payload,
  });
}

/** One inspection an instrument measured on (ImpactItem). */
export interface ImpactItem {
  readonly verificationId: string;
  readonly humanId: string;
  readonly title: string;
  readonly workflowState: string;
  readonly measurementCount: number;
  readonly firstMeasuredAt: string;
}

/** The inspections an instrument measured on: what a lost calibration puts in doubt. */
export async function credentialImpact(
  token: string,
  credentialId: string,
  signal?: AbortSignal,
): Promise<{ status: string; items: ImpactItem[] }> {
  return apiJson(
    token,
    `/credentials/${credentialId}/impact`,
    (raw) => {
      const body = isRecord(raw) ? raw : {};
      return {
        status: str(body.status),
        items: (Array.isArray(body.items) ? body.items : []).filter(isRecord).map((one) => ({
          verificationId: str(one.verification_id),
          humanId: str(one.human_id),
          title: str(one.title),
          workflowState: str(one.workflow_state),
          measurementCount: num(one.measurement_count),
          firstMeasuredAt: str(one.first_measured_at),
        })),
      };
    },
    { signal },
  );
}

/* ---------------------------------------------------------------------- */
/* Ruling 17: the measured value of one conduct line.                      */
/* ---------------------------------------------------------------------- */

export interface Measurement {
  readonly value: string;
  readonly unit: string;
  readonly toleranceMin: string | null;
  readonly toleranceMax: string | null;
  readonly instrumentCredentialId: string | null;
  /** The server's verdict (a database CHECK keeps it true to the numbers). */
  readonly withinTolerance: boolean;
}

/** A decimal as the server wrote it ("12.500000"), without the scale's trailing zeros. */
export function decimalText(raw: string): string {
  return raw.includes(".") ? raw.replace(/\.?0+$/u, "") : raw;
}

export function parseMeasurement(raw: unknown): Measurement | null {
  if (!isRecord(raw)) {
    return null;
  }
  const min = optStr(raw.tolerance_min);
  const max = optStr(raw.tolerance_max);
  return {
    value: decimalText(str(raw.value)),
    unit: str(raw.unit),
    toleranceMin: min === null ? null : decimalText(min),
    toleranceMax: max === null ? null : decimalText(max),
    instrumentCredentialId: optStr(raw.instrument_credential_id),
    withinTolerance: bool(raw.within_tolerance),
  };
}

export interface MeasurementPayload {
  /** Decimals as text so their digits travel exactly. */
  readonly value: string;
  readonly unit: string;
  readonly tolerance_min: string | null;
  readonly tolerance_max: string | null;
  readonly instrument_credential_id: string | null;
}

export async function recordMeasurement(
  token: string,
  verificationId: string,
  lineId: string,
  payload: MeasurementPayload,
): Promise<Measurement> {
  return apiJson(
    token,
    `/verifications/${verificationId}/lines/${lineId}/measurement`,
    (raw) => {
      const found = parseMeasurement(raw);
      if (found === null) {
        throw new Error("Expected a measurement body");
      }
      return found;
    },
    { method: "PUT", body: payload },
  );
}

/* ---------------------------------------------------------------------- */
/* Ruling 19: the EN 10204 classification of a file.                       */
/* ---------------------------------------------------------------------- */

export const DOCUMENT_TYPES = [
  "EN10204_2_1",
  "EN10204_2_2",
  "EN10204_3_1",
  "EN10204_3_2",
  "OTHER",
] as const;

export interface Classification {
  readonly documentType: string;
  readonly heatOrLot: string | null;
  readonly inspectionBodyPartyId: string | null;
}

export function parseClassification(raw: unknown): Classification | null {
  if (!isRecord(raw) || typeof raw.document_type !== "string") {
    return null;
  }
  return {
    documentType: raw.document_type,
    heatOrLot: optStr(raw.heat_or_lot),
    inspectionBodyPartyId: optStr(raw.inspection_body_party_id),
  };
}

export async function classifyAttachment(
  token: string,
  attachmentId: string,
  payload: {
    readonly document_type: string;
    readonly heat_or_lot: string | null;
    readonly inspection_body_party_id: string | null;
  },
): Promise<Classification> {
  return apiJson(
    token,
    `/attachments/${attachmentId}/classification`,
    (raw) => {
      const found = parseClassification(raw);
      if (found === null) {
        throw new Error("Expected a classification body");
      }
      return found;
    },
    { method: "PUT", body: payload },
  );
}

/* ---------------------------------------------------------------------- */
/* Ruling 20: a contract's staffing rules and their shortfalls.            */
/* ---------------------------------------------------------------------- */

export interface StaffingLine {
  readonly ruleId: string;
  readonly roleId: string;
  readonly roleLabel: string;
  readonly disciplineId: string | null;
  readonly disciplineLabel: string | null;
  readonly minCount: number;
  readonly qualifiedCount: number;
  readonly shortfall: number;
  /**
   * The credential requirement behind the rule is enforced at the moment of use (ruling
   * 1 of the integration). No read carries the flag yet: absent reads as unknown (null),
   * and the panel says nothing about it until a read sends it.
   */
  readonly enforced: boolean | null;
}

export interface Staffing {
  readonly lines: readonly StaffingLine[];
  readonly summary: string | null;
}

function staffing(raw: unknown): Staffing {
  const body = isRecord(raw) ? raw : {};
  return {
    summary: optStr(body.summary),
    lines: (Array.isArray(body.lines) ? body.lines : []).filter(isRecord).map((one) => ({
      ruleId: str(one.rule_id),
      roleId: str(one.role_id),
      roleLabel: str(one.role_label),
      disciplineId: optStr(one.discipline_id),
      disciplineLabel: optStr(one.discipline_label),
      minCount: num(one.min_count),
      qualifiedCount: num(one.qualified_count),
      shortfall: num(one.shortfall),
      enforced: typeof one.enforced === "boolean" ? one.enforced : null,
    })),
  };
}

export async function readStaffing(
  token: string,
  contractId: string,
  signal?: AbortSignal,
): Promise<Staffing> {
  return apiJson(token, `/contracts/${contractId}/staffing`, staffing, { signal });
}

export async function addStaffingRule(
  token: string,
  contractId: string,
  payload: {
    readonly role_id: string;
    readonly discipline_id: string | null;
    readonly min_count: number;
  },
): Promise<Staffing> {
  return apiJson(token, `/contracts/${contractId}/staffing-rules`, staffing, {
    method: "POST",
    body: payload,
  });
}
